import { Box } from '@mui/material';
import { format } from 'date-fns';
import { enUS as us, pt } from 'date-fns/locale';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { userInstrumentsSlice } from '!core/redux';
import { marketStockExchangeSlice } from '!views/Market/StockExchange/_marketStockExchange.slice';
import { TrlFavoriteIcon, TrlVariationLabel } from '»components/atoms';
import { ButtonMinus, ButtonPlus } from '»components/atoms/Buttons';
import { useIsTitleNegotiable, useNegModalContext } from '»utils/customHooks';
import { TrlTimeFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { TrlNumberFormatted } from '»utils/number';

import { TrlSimpleChart } from '../TrlSimpleChart';
import {
  BottomContainer,
  ButtonsContainer,
  CardContainer,
  DateLabel,
  Description,
  InfoAndChartContainer,
  LastPx,
  Symbol,
  TimeLabel,
  TopContainer,
  VariationAndChartContainer,
} from './TrlCardDashboardStockExchange.styles';

export const TrlCardDashboardStockExchange = ({ instr }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { openInsertOrderModal } = useNegModalContext();

  const {
    isFavourite,
    instrument,
    symbol,
    exchange,
    securityType,
    descr,
    variationPctg,
    priceRepresentation,
    lastPx,
    lastDateTime,
    canTrade,
    chartData,
  } = instr;

  const isTitleNegotiable = useIsTitleNegotiable({
    isMarketPrimaryScreen: false,
    isFundsPrimaryScreen: false,
    canTrade,
  });

  const isItemFavourite = isFavourite === 'Y';

  const endDecorator = getPercentageDecorator(priceRepresentation);

  const locale = intl.locale.includes('pt') ? pt : us;

  const setFavInstrument = () => {
    dispatch(
      userInstrumentsSlice.actions.setFavInstrumentRequest({
        option: isItemFavourite ? 'D' : 'I',
        instrument,
        symbol,
        exchange,
        securityType,
      }),
    );
  };

  return (
    <CardContainer>
      <TopContainer>
        <TrlFavoriteIcon
          isDisabled={!instrument}
          isFavorite={isItemFavourite}
          onClick={setFavInstrument}
        />
        <InfoAndChartContainer>
          <Description>{descr}</Description>
          <Symbol>{symbol}</Symbol>
          <VariationAndChartContainer>
            <TrlVariationLabel
              variationPctg={variationPctg}
              parent="dashboard"
            />
            <TrlSimpleChart chartData={chartData} isCardDashboardMobileList />
          </VariationAndChartContainer>
        </InfoAndChartContainer>
      </TopContainer>
      <BottomContainer>
        <Box>
          <LastPx>
            <TrlNumberFormatted
              value={lastPx}
              fontFamily="Roboto"
              endDecorator={endDecorator}
              decimalDigits={2}
            />
          </LastPx>

          {lastDateTime ? (
            <Box>
              <DateLabel>
                {format(new Date(lastDateTime), 'dd MMMM yyyy', { locale })}
              </DateLabel>
              <TimeLabel>
                <TrlTimeFormatted
                  value={lastDateTime}
                  hour="numeric"
                  minute="numeric"
                  second="numeric"
                />
              </TimeLabel>
            </Box>
          ) : (
            '-'
          )}
        </Box>
        <ButtonsContainer>
          <ButtonMinus
            disabled={!isTitleNegotiable}
            isCard
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                marketStockExchangeSlice.actions.setSelectedTitle({
                  symbol,
                  exchange,
                  securityType,
                }),
              );

              openInsertOrderModal(2);
            }}
          />
          <ButtonPlus
            disabled={!isTitleNegotiable}
            isCard
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                marketStockExchangeSlice.actions.setSelectedTitle({
                  symbol,
                  exchange,
                  securityType,
                }),
              );

              openInsertOrderModal(1);
            }}
          />
        </ButtonsContainer>
      </BottomContainer>
    </CardContainer>
  );
};

TrlCardDashboardStockExchange.propTypes = {
  instr: PropTypes.object.isRequired,
};
